import { useEffect, useMemo, useState } from 'react'
import {
  X, Loader2, Trash2, CalendarClock, Users, StickyNote, Fingerprint,
  AlertTriangle, Car, CalendarOff, ChevronLeft,
} from 'lucide-react'
import { postJson, patchJson, apiDelete } from '../../lib/api.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import SearchableSelect from '../SearchableSelect.jsx'
import { fullName, indexBranches } from '../../lib/employee.js'
import {
  TIMETABLE_TYPES, shiftTypeTone, relabelBranch, fmtDate, fmtDateTime, isLeaveRow,
} from '../../lib/clockin.js'
import {
  commonWindow, conflictIn, withinRange, codeOf, payloadFrom, validateDraft,
  errText, fmtDayTH, windowOf,
} from '../../lib/employeeSchedule.js'
import { fetchEmployeeMonth } from '../../lib/crossBranch.js'
import { typeAllowed } from '../../lib/employeeMatrix.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10 disabled:bg-slate-50 disabled:text-slate-400'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

function SectionHead({ icon: Icon, title, note }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-2.5">
      <Icon size={15} className="text-brand-600" strokeWidth={1.9} />
      <h3 className="text-[13.5px] font-semibold text-slate-700">{title}</h3>
      {note && <span className="text-[11.5px] text-slate-400">{note}</span>}
    </div>
  )
}

// Same both-sides-stated toggle as the clock-in drawer: "ไม่มีค่าเดินทาง" reads
// as a fact, where an unchecked box reads as an unanswered question.
function Choice({ on, onClick, labelOn, labelOff, tone, dot }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-medium ring-1 transition ${tone}`}>
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      {on ? labelOn : labelOff}
    </button>
  )
}

// The list endpoint resolves created_by/modified_by through an addon that
// outputs only `code`; a bare id is the honest fallback when it cannot.
function actorLabel(actor, id) {
  if (actor?.code) return actor.code
  const n = Number(id)
  return Number.isFinite(n) && n > 0 ? `#${n}` : '—'
}

/**
 * Drawer for one roster row — create (`row` null) or edit.
 *
 * `rows` is the month the grid currently holds. It is not decoration: it is how
 * the drawer can warn about Xano's one-active-row-per-(employee, date) rule
 * BEFORE the save fails, and how a new shift gets the working window this branch
 * actually uses for this type instead of a hard-coded guess.
 *
 * Everything the ลงเวลา tab owns — the punch, allow_OT/special_OT, and the
 * leave flags ลาฉุกเฉิน / ใบรับรองแพทย์ — is absent here on purpose. The proxy
 * carries those columns forward from the stored row, so an edit made here can
 * never blank them.
 */
export default function EmployeeShiftEditor({
  row, draft: seed, rows = [], range, branches = [], employees = [], onClose, onSaved, onDeleted,
  onBack, backLabel,
}) {
  const [draft, setDraft] = useState(seed)
  // This person's whole month, every branch (lib/crossBranch.js). `null` until
  // a person is chosen and the lookup lands.
  const [personRows, setPersonRows] = useState(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const editing = Number(row?.id) > 0
  const leave = isLeaveRow(draft)
  const branchName = useMemo(() => indexBranches(branches), [branches])
  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])

  const empOptions = useMemo(() => employees.map((e) => ({
    value: String(e.id),
    // Code is the roster key at this clinic (a readable "นิกเนม ชื่อจริง"), so it
    // is the label; the formal name stays in `hint`, which the picker searches
    // on but does not render.
    label: e.code || fullName(e),
    hint: [fullName(e), e.nickname, e.name_eng].filter(Boolean).join(' · '),
  })), [employees])

  useEffect(() => {
    // Esc steps BACK when there is somewhere to step back to; only a top-level
    // shift closes outright.
    const onKey = (e) => { if (e.key === 'Escape' && !confirmDelete) (onBack || onClose)?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, onBack, confirmDelete])

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))

  const personRange = useMemo(() => {
    const m = /^(\d{4})-(\d{2})/.exec(String(draft.date || ''))
    if (!m) return null
    const last = new Date(Number(m[1]), Number(m[2]), 0).getDate()
    return { start_date: `${m[1]}-${m[2]}-01`, end_date: `${m[1]}-${m[2]}-${String(last).padStart(2, '0')}` }
  }, [draft.date])

  /* One small request per person-month, cached and shared with the matrix.
   * It is what makes the duplicate check TRUE: `rows` is one branch, and Xano's
   * one-row-per-(employee, date) rule is clinic-wide, so a day that looks free
   * here may already be taken at another branch — a save that fails after the
   * form is filled in, instead of a warning while it is being filled. */
  useEffect(() => {
    const id = Number(draft.employee_id) || 0
    if (!id || !personRange) { setPersonRows(null); return }
    let on = true
    setPersonRows(null)
    fetchEmployeeMonth(id, personRange).then((r) => { if (on && r) setPersonRows(r) })
    return () => { on = false }
  }, [draft.employee_id, personRange?.start_date, personRange?.end_date]) // eslint-disable-line react-hooks/exhaustive-deps

  // This person's month, as the roster sees it — the number that keeps a month
  // fair, shown at the moment it is being changed rather than in a report.
  const personStats = useMemo(() => {
    if (!personRows) return null
    let shifts = 0; let leave = 0; let here = 0
    for (const r of personRows) {
      if (isLeaveRow(r)) { leave += 1; continue }
      shifts += 1
      if (Number(r.branch_id) === Number(draft.branch_id)) here += 1
    }
    return { shifts, leave, here }
  }, [personRows, draft.branch_id])

  // Switching type re-seeds the window from what this branch actually runs for
  // that type — but only when the operator has not typed one, so a deliberate
  // 08:00 survives a กะเช้า → กะควบ correction.
  function pickType(type) {
    const cur = commonWindow(rows, draft.type, draft.branch_id)
    const untouched = !editing && (!draft.start || !draft.end || (draft.start === cur.start && draft.end === cur.end))
    const w = commonWindow(rows, type, draft.branch_id)
    set(untouched ? { type, start: w.start, end: w.end } : { type })
  }

  /* The clash this row would cause, found in the loaded grid. Outside the loaded
   * range we cannot know — the proxy re-checks server-side and its Thai message
   * lands in `err`, so silence here is honest rather than a false all-clear. */
  const clash = useMemo(
    () => conflictIn(rows, draft) || conflictIn(personRows || [], draft),
    [rows, personRows, draft],
  )
  // Knowable either because the grid covers the day, or because we hold this
  // person's whole month — the second is the stronger of the two.
  const clashKnowable = !!personRows || withinRange(draft.date, range || { start_date: '', end_date: '' })
  const invalid = validateDraft(draft)
  const blocked = !!clash || !!invalid

  // The stored row came back without a window (start_date is a `private` column
  // on the table, so it may not survive the API's field filtering). Say so
  // rather than silently presenting the standard hours as if they were the
  // shift's own — pressing บันทึก would then rewrite them.
  // "Edited" means a different hand or a different minute — a create stamps
  // both pairs identically, and repeating them reads as two separate events.
  const edited = editing && (
    Number(row?.modified_by) !== Number(row?.created_by)
    || Math.abs(Number(row?.modified_at || 0) - Number(row?.created_at || 0)) > 60_000
  )

  const windowMissing = editing && !leave && (() => { const w = windowOf(row); return !w.start || !w.end })()

  async function save() {
    if (blocked) { setErr(invalid || 'พนักงานคนนี้มีตารางเวรในวันดังกล่าวอยู่แล้ว'); return }
    setSaving(true); setErr('')
    try {
      const payload = payloadFrom(draft)
      if (editing) await patchJson(`/api/hr/schedule/employee/${row.id}`, payload)
      else await postJson('/api/hr/schedule/employee', payload)
      onSaved?.()
    } catch (e) { setErr(errText(e)) }
    finally { setSaving(false) }
  }

  async function remove() {
    setConfirmDelete(false)
    setSaving(true); setErr('')
    try {
      await apiDelete(`/api/hr/schedule/employee/${row.id}`)
      onDeleted?.()
    } catch (e) { setErr(errText(e, 'ลบเวรไม่สำเร็จ')) }
    finally { setSaving(false) }
  }

  const headTitle = editing
    ? (codeOf(row, empById) || 'แก้ไขเวร')
    : 'เพิ่มเวรพนักงาน'

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="relative w-full max-w-[560px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="min-w-0">
            {/* Only when this shift was opened from a person's month — the way
                back to the list you were reading, rather than a close that
                drops you at the grid and makes you find them again. */}
            {onBack && (
              <button onClick={onBack}
                className="mb-0.5 -ml-1 inline-flex items-center gap-0.5 rounded-lg px-1 py-0.5 text-[12px] text-slate-500 hover:text-brand-700 hover:bg-brand-50">
                <ChevronLeft size={13} /> เวรทั้งเดือน{backLabel ? ` · ${backLabel}` : ''}
              </button>
            )}
            <h2 className="text-[16px] font-semibold text-slate-900 truncate">{headTitle}</h2>
            <p className="text-[12.5px] text-slate-500 truncate">
              {fmtDayTH(draft.date)}{draft.type ? ` · ${draft.type}` : ''}
              {draft.branch_id ? ` · ${relabelBranch(branchName.get(Number(draft.branch_id)) || '')}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <SectionHead icon={Users} title="พนักงานและสาขา" />
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className={LABEL}>พนักงาน</label>
              <SearchableSelect
                options={empOptions}
                value={draft.employee_id ? String(draft.employee_id) : ''}
                onChange={(v) => set({ employee_id: Number(v) || 0 })}
                placeholder="เลือกพนักงาน"
                searchPlaceholder="พิมพ์รหัสพนักงาน / ชื่อ…"
              />
              {personStats && (
                <p className="mt-1.5 text-[11.5px] text-slate-500 tabular-nums">
                  เดือนนี้ <span className="font-semibold text-slate-700">{personStats.shifts}</span> เวร
                  {personStats.here !== personStats.shifts && ` (สาขานี้ ${personStats.here})`}
                  {personStats.leave > 0 && <span className="text-amber-600"> · ลา {personStats.leave} วัน</span>}
                </p>
              )}
            </div>
            <div>
              <label className={LABEL}>สาขาที่เข้าเวร</label>
              <select value={draft.branch_id || ''} onChange={(e) => set({ branch_id: Number(e.target.value) || 0 })} className={FIELD}>
                <option value="">เลือกสาขา</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
              </select>
            </div>
          </div>

          <SectionHead icon={CalendarClock} title="ประเภทเวรและเวลา" />
          <div className="flex flex-wrap gap-1.5">
            {TIMETABLE_TYPES.map((t) => {
              const on = draft.type === t
              // เวรผู้ช่วย is chair-side work, offered only to staff whose role
              // list carries Assistance — the same rule the matrix applies, so
              // this drawer cannot be the way around it.
              const allowed = typeAllowed(t, empById.get(Number(draft.employee_id)))
              return (
                <button key={t} type="button" onClick={() => pickType(t)} disabled={!allowed && !on}
                  title={allowed ? '' : 'พนักงานคนนี้ไม่มีสิทธิ์ Assistance — กำหนดเวรผู้ช่วยไม่ได้'}
                  className={`rounded-full px-3 py-1.5 text-[12.5px] font-medium ring-1 transition ${
                    on ? `${shiftTypeTone(t)} ring-2 ring-offset-1 ring-brand-400`
                      : !allowed ? 'bg-slate-50 text-slate-300 ring-slate-200 cursor-not-allowed'
                        : 'bg-white text-slate-500 ring-slate-200 hover:bg-slate-50'}`}>
                  {t}
                </button>
              )
            })}
          </div>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <div className={leave ? 'col-span-3' : ''}>
              <label className={LABEL}>วันที่</label>
              <input type="date" value={draft.date || ''} onChange={(e) => set({ date: e.target.value })} className={FIELD} />
            </div>
            {/* A leave day has no working window — the inputs are removed rather
                than disabled, because there is no time here to look at. */}
            {!leave && (
              <>
                <div>
                  <label className={LABEL}>เข้าเวร</label>
                  <input type="time" value={draft.start || ''} onChange={(e) => set({ start: e.target.value })} className={FIELD} />
                </div>
                <div>
                  <label className={LABEL}>ออกเวร</label>
                  <input type="time" value={draft.end || ''} onChange={(e) => set({ end: e.target.value })} className={FIELD} />
                </div>
              </>
            )}
          </div>

          {leave && (
            <div className="mt-2.5 flex items-start gap-2 rounded-xl bg-violet-50 ring-1 ring-violet-200 px-3 py-2.5 text-[12.5px] text-violet-800">
              <CalendarOff size={15} className="mt-0.5 shrink-0" />
              <span>วัน{draft.type}ไม่มีเวลาเข้า-ออกเวร · ลาฉุกเฉินและใบรับรองแพทย์บันทึกได้ที่แท็บ “ลงเวลา”</span>
            </div>
          )}

          {windowMissing && (
            <div className="mt-2.5 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12px] text-amber-800">
              ไม่พบเวลาเข้า-ออกเดิมของเวรนี้ — หากกดบันทึก ระบบจะใช้เวลาที่แสดงอยู่แทน
            </div>
          )}

          {clash && (
            <div className="mt-2.5 flex items-start gap-2 rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3 py-2.5 text-[12.5px] text-rose-800">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>
                พนักงานคนนี้มีตารางเวร <span className="font-semibold">{clash.type}</span>
                {Number(clash.branch_id) !== Number(draft.branch_id) && (
                  <> ที่สาขา <span className="font-semibold">{relabelBranch(branchName.get(Number(clash.branch_id)) || '') || '—'}</span></>
                )}
                {' '}ในวันที่ {fmtDate(clash.date)} อยู่แล้ว —
                หนึ่งคนมีได้วันละ 1 เวรทั้งคลินิก กรุณาแก้ไขเวรเดิมแทน
              </span>
            </div>
          )}
          {!clash && !clashKnowable && draft.date && (
            <p className="text-[11.5px] text-slate-400 mt-2">
              วันที่นี้อยู่นอกเดือนที่แสดงอยู่ — ระบบจะตรวจสอบเวรซ้ำอีกครั้งตอนบันทึก
            </p>
          )}

          <SectionHead icon={Car} title="ค่าเดินทาง" note="เข้าไปรวมในเงินเดือน" />
          <Choice
            on={draft.travel_fee}
            onClick={() => set({ travel_fee: !draft.travel_fee })}
            labelOn="มีค่าเดินทาง" labelOff="ไม่มีค่าเดินทาง"
            tone={draft.travel_fee ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-50 text-slate-500 ring-slate-200'}
            dot={draft.travel_fee ? 'bg-emerald-500' : 'bg-slate-300'}
          />
          {draft.travel_fee && (
            <div className="mt-2">
              <label className={LABEL}>จำนวน (บาท)</label>
              <input type="number" min="0" step="1" inputMode="numeric"
                value={draft.travel_fee_amount || ''}
                onChange={(e) => set({ travel_fee_amount: e.target.value })}
                className={`${FIELD} max-w-[180px] tabular-nums`} />
            </div>
          )}

          <SectionHead icon={StickyNote} title="หมายเหตุ" />
          <textarea rows={2} value={draft.remark || ''} onChange={(e) => set({ remark: e.target.value })}
            placeholder="เช่น สลับเวรกับ…, มาช่วยสาขาอื่น" className={FIELD} />

          {/* Provenance, one muted line: it is what you check when a shift looks
              wrong, not something you read on the way past — and a three-row
              bordered panel gave it the weight of a field you are meant to fill
              in. The edit half is dropped when nothing has been edited yet,
              which on a fresh row is the same person and the same minute. */}
          {editing && (
            <p className="mt-6 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-slate-400">
              <span className="inline-flex items-center gap-1" title="employee_timetable">
                <Fingerprint size={11} /> #{row.id}
              </span>
              <span>· สร้าง {actorLabel(row?.creator, row?.created_by)} {fmtDateTime(row?.created_at)}</span>
              {edited && (
                <span>· แก้ไข {actorLabel(row?.modifier, row?.modified_by)} {fmtDateTime(row?.modified_at)}</span>
              )}
            </p>
          )}
        </div>

        <div className="border-t border-slate-100 px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {editing && (
              <button onClick={() => setConfirmDelete(true)} disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                <Trash2 size={14} /> ลบเวร
              </button>
            )}
            {/* The save button is disabled while the draft is incomplete, so the
                reason has to be visible somewhere — a dead button with no
                explanation is the worst of both. */}
            {err
              ? <span className="text-[12.5px] text-rose-500 truncate">{err}</span>
              : invalid ? <span className="text-[12.5px] text-slate-400 truncate">{invalid}</span> : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ปิด</button>
            <button onClick={save} disabled={saving || blocked} title={invalid || (clash ? 'มีเวรซ้ำในวันเดียวกัน' : '')}
              className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saving && <Loader2 size={14} className="animate-spin" />} บันทึก
            </button>
          </div>
        </div>
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="ลบเวรนี้"
          message="เวรจะถูกปิดใช้งานและหายไปจากตารางเวร รายงานการลงเวลา และการคำนวณเงินเดือน (ข้อมูลเดิมยังเก็บไว้ในระบบ)"
          confirmLabel="ลบเวร"
          tone="danger"
          onConfirm={remove}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  )
}
